import { ref } from 'vue';
import { servicesAPI, type Service, type ServiceCreate, type ServiceUpdate } from '../services/api';
import { getI18nT } from '../i18n';

const services = ref<Service[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
const currentPage = ref(1);
const totalPages = ref(1);
const perPage = ref(10);
const totalCount = ref(0);
const hasMore = ref(true);

export function useServices() {
  const fetchServices = async (page: number = 1, limit: number = 10): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await servicesAPI.getAll({ page, per_page: limit });

      services.value = page === 1 ? response.services : [...services.value, ...response.services];
      currentPage.value = response.page;
      perPage.value = response.per_page;
      totalCount.value = response.total;
      hasMore.value = response.has_more;
      totalPages.value = Math.ceil(response.total / response.per_page);

      return true;
    } catch (err: any) {
      console.error('Error fetching services:', err);
      const t = getI18nT();
      error.value = err.response?.data?.message || t('composables.services.error_fetch');
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchServiceById = async (id: string): Promise<Service | null> => {
    isLoading.value = true;
    error.value = null;

    try {
      const service = await servicesAPI.getById(id);
      return service;
    } catch (err: any) {
      console.error('Error fetching service:', err);
      const t = getI18nT();
      error.value = err.response?.data?.message || t('composables.services.error_fetch_one');
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const createService = async (data: ServiceCreate): Promise<Service | null> => {
    isLoading.value = true;
    error.value = null;

    try {
      const service = await servicesAPI.create(data);
      // Add to the list
      services.value.unshift(service);
      totalCount.value++;
      return service;
    } catch (err: any) {
      console.error('Error creating service:', err);
      const t = getI18nT();
      error.value = err.response?.data?.message || t('composables.services.error_create');
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const updateService = async (id: string, data: ServiceUpdate): Promise<Service | null> => {
    isLoading.value = true;
    error.value = null;

    try {
      const service = await servicesAPI.update(id, data);
      // Update in the list
      const index = services.value.findIndex((s) => s.id === id);
      if (index !== -1) {
        services.value[index] = service;
      }
      return service;
    } catch (err: any) {
      console.error('Error updating service:', err);
      const t = getI18nT();
      error.value = err.response?.data?.message || t('composables.services.error_update');
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteService = async (id: string): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      await servicesAPI.delete(id);
      // Remove from the list
      services.value = services.value.filter((s) => s.id !== id);
      totalCount.value--;
      return true;
    } catch (err: any) {
      console.error('Error deleting service:', err);
      const t = getI18nT();
      error.value = err.response?.data?.message || t('composables.services.error_delete');
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const clearServices = () => {
    services.value = [];
    currentPage.value = 1;
    totalCount.value = 0;
    hasMore.value = true;
  };

  const getServiceById = (id: string): Service | undefined => {
    return services.value.find((s) => s.id === id);
  };

  return {
    services,
    isLoading,
    error,
    currentPage,
    totalPages,
    perPage,
    totalCount,
    hasMore,
    fetchServices,
    fetchServiceById,
    createService,
    updateService,
    deleteService,
    clearServices,
    getServiceById,
  };
}
